import { Button, Drawer, Form, Select } from 'antd';
import { useLiveQuery } from 'dexie-react-hooks';
import { useTranslation } from 'translations/hooks';
import { db } from 'technical/db';
import { SynchronizationTag } from 'business/tunnel/pages/ring-scan/components/synchronization-tag';

interface Props {
  isOpen: boolean;
  serialNumber: string | null;
  onConfirm: (segmentType: string) => void;
  onCancel: () => void;
}

export const NewSegmentDrawer = ({
  isOpen,
  serialNumber,
  onConfirm,
  onCancel,
}: Props) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<{ segmentType: string }>();
  const selectedType = Form.useWatch('segmentType', form);

  const segmentDrawConfigs = useLiveQuery(
    () => db.segmentDrawConfigs.toArray(),
    [],
  );

  const options = (segmentDrawConfigs ?? []).map((config) => ({
    value: config.label,
    label: config.label,
  }));

  const handleConfirm = () => {
    if (selectedType) {
      form.setFieldValue('segmentType', null);
      onConfirm(selectedType);
    }
  };

  return (
    <Drawer open={isOpen} placement="bottom" onClose={onCancel}>
      <Form
        form={form}
        key={serialNumber ?? 'new'}
        className="flex h-full flex-col gap-4"
      >
        <p className="text-primary text-center text-lg font-medium">
          {t('tunnel.scanner.newSegmentDrawer.title')}
        </p>

        <div className="rounded-lg border p-3">
          <div className="grid grid-cols-[1fr_2.5rem_auto] items-center gap-2">
            <span className="truncate font-medium">{serialNumber}</span>
            <span className="truncate text-gray-500">-</span>
            <SynchronizationTag isSynced={false} />
          </div>
        </div>

        <Form.Item name="segmentType" noStyle>
          <Select
            options={options}
            placeholder={t('common.placeholder.select')}
            className="w-full text-base"
          />
        </Form.Item>

        <div className="mt-auto flex flex-col gap-3">
          <Button
            type="primary"
            className="h-11 w-full"
            disabled={!selectedType}
            onClick={handleConfirm}
          >
            {t('common.validate')}
          </Button>
          <Button
            color="primary"
            variant="outlined"
            className="h-11 w-full"
            onClick={onCancel}
          >
            {t('common.cancel')}
          </Button>
        </div>
      </Form>
    </Drawer>
  );
};
